import {
  assessVerticalRelation,
  classifyInstallationTypology,
  describeRule,
  findReadingElement,
  footprintsOverlap,
  isSuspendedTypology,
  planEnvironmentIssues,
  planSubjectsRefer,
  spatialContractIssues,
  verticalClearanceRule,
  type EnvironmentReading,
  type InstallationTypology,
  type OpeningKind,
  type TaskPlan,
  type TaskSpatialDecision,
  type TypologyDescriptor,
} from "@calcplan/ai-capabilities";

export type EnvironmentReviewAttribute = "position" | "dimensions" | "orientation" | "material" | "product" | "quantity" | "relation" | "function" | "appearance";
export type ProfessionalDesignAspect = "proportion" | "scale" | "alignment" | "spacing" | "rhythm" | "coherence" | "material" | "function" | "installation" | "brief_fidelity" | "use_height" | "operation" | "datum_alignment";

/** O que a tipologia e o estado do alvo dizem ao crítico, independentemente do plano. */
export interface ReviewTargetProfile {
  kind: InstallationTypology;
  suspended: boolean;
  openingKind?: OpeningKind;
}

export interface EnvironmentReviewIssue {
  elementId: string;
  attribute: EnvironmentReviewAttribute;
  kind: "brief_mismatch" | "technical" | "agent_design";
  summary: string;
}

export interface EnvironmentIssueDisposition {
  actionable: boolean;
  reason: "changed-technical" | "changed-brief-mismatch" | "agent-owned" | "explicitly-protected" | "outside-turn" | "authority-unproven";
}

/**
 * Baseline for a blind design critic. It is intentionally independent of the
 * planner's self-authored criteria and of any particular room or object type.
 * The typology of the target adds what a professional always checks for that
 * family — a suspended element is judged by the height people use it at, by
 * the lines the wall already has and by where its doors or flaps swing —
 * whatever the planner chose to write in its own criteria.
 */
export function independentAgentDesignReviewAspects(
  attributes: ReadonlyArray<EnvironmentReviewAttribute>,
  profile?: ReviewTargetProfile | null,
): ProfessionalDesignAspect[] {
  if (!attributes.length) return [];
  const aspects: ProfessionalDesignAspect[] = [
    "proportion",
    "scale",
    "alignment",
    "spacing",
    "coherence",
    "function",
    "brief_fidelity",
  ];
  const spatial = attributes.some((attribute) => ["position", "dimensions", "orientation", "relation"].includes(attribute));
  if (spatial) aspects.push("installation");
  if (attributes.some((attribute) => attribute === "material" || attribute === "appearance")) aspects.push("material");
  if (spatial && profile && (profile.suspended || isSuspendedTypology(profile.kind))) {
    aspects.push("datum_alignment");
    if (["upper_cabinet", "shelf", "mirror_or_art", "hood", "lighting", "unknown"].includes(profile.kind)) aspects.push("use_height");
  }
  if (spatial && profile?.openingKind && ["top_hinged", "side_hinged", "lift_up"].includes(profile.openingKind)) {
    aspects.push("operation");
  }
  return [...new Set(aspects)];
}

export interface UseReviewResult {
  /** Bloqueiam a aprovação; quando o plano é a causa, trazem o marcador de emenda. */
  issues: string[];
  /** Registradas no laudo como observações: sem plano não há contrato a emendar. */
  observations: string[];
}

/**
 * Régua da pessoa e da parede aplicada ao elemento como ele ficou: cota de uso
 * pela atividade da superfície de baixo, linha existente ou nova justificada,
 * alcance do conteúdo, envelope de operação. Reaproveita exatamente a mesma
 * validação do plano (planEnvironmentIssues) sobre a cota observada — o que o
 * planejador aceitou com justificativa o revisor não reabre; o que o plano não
 * justificou volta como emenda, nunca como nudge.
 */
export function independentUseIssues(input: {
  plan: TaskPlan | undefined;
  elementName: string;
  reading: EnvironmentReading;
  elementId: string;
  enforce: boolean;
}): UseReviewResult {
  if (!input.enforce) return { issues: [], observations: [] };
  const element = input.reading.elements.find((candidate) => candidate.elementId === input.elementId)
    ?? findReadingElement(input.reading, input.elementName);
  if (!element || !element.suspended) return { issues: [], observations: [] };
  const planned = input.plan?.spatialDecisions.find((decision) => (
    ["above", "below"].includes(decision.relation)
    && (decision.subjectElementId === input.elementId || planSubjectsRefer(decision.subject, input.elementName))
  )) ?? null;
  // A referência real: o que o plano nomeou (por id, depois por nome); na
  // falta, o elemento de apoio logo abaixo — abaixo de verdade, com
  // sobreposição em planta, não apenas na mesma parede: um sofá mais alto no
  // outro extremo da parede não é a referência de um aéreo sobre o aparador.
  const below = input.reading.elements
    .filter((candidate) => (
      candidate.elementId !== element.elementId
      && !candidate.suspended
      && candidate.topMm <= element.bottomMm + 1
      && (element.footprintMm && candidate.footprintMm
        ? footprintsOverlap(candidate.footprintMm, element.footprintMm)
        : candidate.wallId === element.wallId)
    ))
    .sort((left, right) => right.topMm - left.topMm)[0] ?? null;
  const plannedById = planned?.referenceElementId
    ? input.reading.elements.find((candidate) => candidate.elementId === planned.referenceElementId) ?? null
    : null;
  const reference = plannedById ?? (planned ? findReadingElement(input.reading, planned.reference) ?? below : below);
  if (!reference) return { issues: [], observations: [] };
  // When the plan already decided an absolute cota and the element does not
  // sit on it, the defect is execution, not the plan: the element has to be
  // moved to the plan's cota. Asking for an amendment here sent the executor
  // into "amend → nothing changed → rejected" until the breaker closed the
  // turn (19/08 18:13), while another message correctly asked for the nudge.
  const plannedBottom = planned?.bottomHeightMm ?? null;
  const plannedTop = planned?.topHeightMm ?? null;
  const executionTolerance = 15;
  const bottomOff = plannedBottom !== null && Math.abs(element.bottomMm - plannedBottom) > executionTolerance;
  const topOff = plannedTop !== null && Math.abs(element.topMm - plannedTop) > executionTolerance;
  if (bottomOff || topOff) {
    const deltaMm = plannedBottom !== null ? plannedBottom - element.bottomMm : (plannedTop ?? element.topMm) - element.topMm;
    return {
      issues: [`“${element.name}” está com a face inferior a ${Math.round(element.bottomMm)} mm e o topo a ${Math.round(element.topMm)} mm, mas o plano decidiu ${plannedBottom !== null ? `face inferior a ${Math.round(plannedBottom)} mm` : `topo a ${Math.round(plannedTop!)} mm`}${planned?.datum ? ` (datum ${planned.datum.kind}${planned.datum.reference ? `: ${planned.datum.reference}` : ""})` : ""}. O plano já está decidido: não o emende de novo — mova o mesmo elemento para a cota com scene.element.nudge deltaMm [0, ${Math.round(deltaMm)}, 0] (Y é o eixo vertical; preserve X e Z) e confira de novo.`],
      observations: [],
    };
  }
  const decision: TaskSpatialDecision = {
    subject: element.name,
    relation: "above",
    reference: reference.name,
    referenceElementId: reference.elementId,
    support: element.mount === "ceiling" ? "ceiling" : "wall",
    clearanceMm: planned?.clearanceMm ?? null,
    allowContact: planned?.allowContact ?? false,
    bottomHeightMm: element.bottomMm,
    topHeightMm: element.topMm,
    datum: planned?.datum ?? null,
    source: planned?.source ?? "inferred",
    rationale: planned?.rationale ?? "Estado observado na cena para a revisão de uso.",
  };
  // The element's own top and bottom are lines of the wall too; judging it
  // against itself would make every cota "aligned to an existing line".
  const readingWithoutSelf: EnvironmentReading = {
    ...input.reading,
    walls: input.reading.walls.map((wall) => ({
      ...wall,
      lines: wall.lines
        .filter((line) => {
          const sources = line.sourceElementIds ?? (line.sourceElementId ? [line.sourceElementId] : []);
          return sources.length === 0 || sources.some((id) => id !== element.elementId);
        })
        .map((line) => (line.sourceElementIds?.includes(element.elementId)
          ? { ...line, sourceElementIds: line.sourceElementIds.filter((id) => id !== element.elementId), sourceElementId: line.sourceElementIds.find((id) => id !== element.elementId) }
          : line)),
    })),
  };
  const environmentIssues = planEnvironmentIssues(
    { spatialDecisions: [decision], designResolutions: input.plan?.designResolutions ?? [], assumptions: input.plan?.assumptions ?? [] },
    readingWithoutSelf,
    {
      subjectHeightMm: () => element.topMm - element.bottomMm,
      subjectDescriptor: () => ({ name: element.name, dimensionsMm: { width: element.widthMm, height: element.topMm - element.bottomMm, depth: element.depthMm } }),
    },
  );
  const issues: string[] = [];
  const observations: string[] = [];
  for (const issue of environmentIssues) {
    if (!input.plan) {
      observations.push(issue.message);
      continue;
    }
    issues.push(`${PLAN_AMENDMENT_REQUIRED_MARKER} ${issue.message} A cota é decisão do plano: emende a decisão espacial (task.plan.amend com bottomHeightMm/topHeightMm e datum, ou justificativa de linha nova) antes de mover o elemento.`);
  }
  return { issues, observations };
}

export function professionalEnvironmentEvidence(input: {
  overviewComplete: boolean;
  capturedTargetIds: string[];
  changedIds: ReadonlySet<string>;
  verdictIds: ReadonlySet<string>;
}): { ready: boolean; missingVerdictIds: string[]; issues: string[] } {
  const missingVerdictIds = input.capturedTargetIds
    .filter((id) => input.changedIds.has(id) && !input.verdictIds.has(id));
  const issues = [
    ...(!input.overviewComplete
      ? ["A revisão final precisa conter panorama 3D e planta global antes de aprovar o ambiente."]
      : []),
    ...missingVerdictIds.map((id) => `${id}: o alvo alterado não recebeu um laudo novo na revisão panorâmica final.`),
  ];
  return { ready: issues.length === 0, missingVerdictIds, issues };
}

export interface InstallationReviewSnapshot {
  support: {
    expected: "wall" | "floor" | "ceiling" | "element" | "unconstrained";
    status: "supported" | "gap" | "penetrating" | "unsupported";
  };
  nearestElements: Array<{
    name: string;
    planGapMm: number;
    verticalGapMm: number;
    planOverlapMm: number;
    verticalOverlapMm: number;
    position: "above" | "below" | "beside";
    relation: "separate" | "touching" | "overlapping";
    /** Ponto de instalação embutido (tomada, interruptor, registro) atrás ou dentro do volume: interferência, não colisão. */
    fixture?: boolean;
  }>;
}

/** Observações de instalação para pontos embutidos que o elemento cobre — recorte ou acesso a prever, nunca reprovação. */
export function fixtureInterferenceObservations(elementName: string, installation: InstallationReviewSnapshot): string[] {
  return installation.nearestElements
    .filter((neighbor) => neighbor.fixture && (neighbor.relation === "overlapping" || neighbor.relation === "touching"))
    .map((neighbor) => `“${elementName}” cobre o ponto de instalação “${neighbor.name}” (${neighbor.relation === "overlapping" ? `sobreposição de ${Math.round(Math.min(neighbor.planOverlapMm, neighbor.verticalOverlapMm))} mm` : "encosto"}): interferência de instalação a prever no detalhamento (recorte no fundo ou acesso), não colisão; não afaste o móvel da parede por causa dela.`);
}

export interface InstallationReviewDescriptors {
  /** Dados do próprio elemento (nome, categoria de catálogo, dimensões) para classificar a tipologia. */
  element?: TypologyDescriptor;
  /** Dados dos vizinhos, indexados pelo nome usado em `nearestElements`. */
  neighbors?: Record<string, TypologyDescriptor>;
  /**
   * Whether the typology rule may be applied to neighbours the plan is silent
   * about (or when there is no plan). True for elements created or freely
   * implanted in this turn; false when the designer dictated the numbers.
   */
  enforceTypology?: boolean;
}

export const PLAN_AMENDMENT_REQUIRED_MARKER = "[emenda-de-plano-necessária]";

/**
 * Independent, deterministic checks that the visual critic cannot waive by
 * merely declaring its own design successful.
 *
 * Two guarantees keep this reviewer from driving the executor in circles:
 * it never dictates a correction that would push a suspended element below the
 * professional minimum of its typology (the "nudge back to zero" that undid
 * every fix the visual critic had just demanded), and when the plan's own
 * spatial contract cannot be satisfied it asks for a plan amendment instead
 * of alternating between two contradictory rules.
 */
export function independentInstallationIssues(
  plan: TaskPlan | undefined,
  elementName: string,
  installation: InstallationReviewSnapshot,
  descriptors: InstallationReviewDescriptors = {},
): string[] {
  const issues: string[] = [];
  if (["wall", "ceiling"].includes(installation.support.expected)
    && installation.support.status !== "supported") {
    issues.push(`A fixação esperada em ${installation.support.expected} não está comprovada (${installation.support.status}).`);
  }
  if (installation.support.expected === "floor" && installation.support.status !== "supported") {
    issues.push(`O apoio esperado no piso não está comprovado (${installation.support.status}).`);
  }
  for (const neighbor of installation.nearestElements) {
    if (neighbor.relation === "overlapping" && !neighbor.fixture) issues.push(`Há sobreposição geométrica com “${neighbor.name}”.`);
  }

  const elementDescriptor: TypologyDescriptor = descriptors.element ?? { name: elementName };
  const elementTypology = classifyInstallationTypology(elementDescriptor);
  const neighborDescriptor = (name: string): TypologyDescriptor => descriptors.neighbors?.[name] ?? { name };
  const suspendedSupport = ["wall", "ceiling"].includes(installation.support.expected);
  const assessedNeighbors = new Set<string>();

  const verticalDecisions = plan?.spatialDecisions.filter((decision) =>
    ["above", "below", "stacked"].includes(decision.relation)
    && textRefersTo(decision.subject, elementName)) ?? [];
  for (const decision of verticalDecisions) {
    const expectedPosition = decision.relation === "below" ? "above" : "below";
    const candidates = installation.nearestElements
      .filter((neighbor) => neighbor.position === expectedPosition && neighbor.planOverlapMm > 1)
      .sort((left, right) => Number(textRefersTo(decision.reference, right.name)) - Number(textRefersTo(decision.reference, left.name)));
    const neighbor = candidates[0];
    if (!neighbor) continue;
    assessedNeighbors.add(neighbor.name);
    const neighborTypology = classifyInstallationTypology(neighborDescriptor(neighbor.name));
    const rule = decision.relation === "below"
      ? verticalClearanceRule({ upper: neighborTypology.kind, lower: elementTypology.kind, relation: "above", support: decision.support })
      : verticalClearanceRule({ upper: elementTypology.kind, lower: neighborTypology.kind, relation: decision.relation as "above" | "stacked", support: decision.support });
    const upperLabel = decision.relation === "below" ? `“${neighbor.name}”` : `“${elementName}”`;
    const lowerLabel = decision.relation === "below" ? `“${elementName}”` : `“${neighbor.name}”`;

    const contractIssues = spatialContractIssues({
      subject: decision.subject,
      reference: decision.reference,
      relation: decision.relation,
      support: decision.support,
      clearanceMm: decision.clearanceMm,
      allowContact: decision.allowContact,
      source: decision.source,
      subjectDescriptor: elementDescriptor,
      referenceDescriptor: neighborDescriptor(neighbor.name),
    });
    if (contractIssues.length) {
      // The plan itself is unsatisfiable or unprofessional. Correcting the
      // element toward it would only restart the loop; the plan has to change.
      issues.push(`${PLAN_AMENDMENT_REQUIRED_MARKER} O contrato espacial do plano para “${decision.subject}” não pode ser executado como está: ${contractIssues.join(" ")} Registre a emenda com task.plan.amend (nova folga ou novo suporte, com justificativa) antes de corrigir o elemento.`);
      const assessment = assessVerticalRelation({
        rule,
        gapMm: neighbor.verticalGapMm,
        touching: neighbor.relation === "touching",
        upperLabel,
        lowerLabel,
      });
      if (assessment.message) issues.push(assessment.message);
      continue;
    }

    if (decision.clearanceMm
      && (neighbor.verticalGapMm < decision.clearanceMm.minimum - 1
        || neighbor.verticalGapMm > decision.clearanceMm.maximum + 1)) {
      const correctionMm = decision.relation === "below"
        ? neighbor.verticalGapMm - decision.clearanceMm.target
        : decision.clearanceMm.target - neighbor.verticalGapMm;
      const resultingGap = decision.clearanceMm.target;
      const belowTypologyMinimum = Boolean(rule && !rule.contactAllowed && resultingGap < rule.minimumMm);
      if (belowTypologyMinimum && rule) {
        issues.push(`${PLAN_AMENDMENT_REQUIRED_MARKER} A folga-alvo do plano (${decision.clearanceMm.target} mm) entre ${upperLabel} e ${lowerLabel} fica abaixo do mínimo profissional de ${rule.minimumMm} mm (${describeRule(rule)}). Não reduza a folga: emende a decisão espacial com task.plan.amend para dentro da faixa típica e só então ajuste o elemento.`);
      } else {
        issues.push(`A folga vertical para “${neighbor.name}” ficou em ${roundMm(neighbor.verticalGapMm)} mm; o plano exige ${decision.clearanceMm.minimum}–${decision.clearanceMm.maximum} mm. Como Y é o eixo vertical, use scene.element.nudge no alvo com deltaMm [0, ${roundMm(correctionMm)}, 0] para atingir a folga-alvo de ${decision.clearanceMm.target} mm, preservando X e Z.`);
      }
    }
    if (!decision.allowContact && neighbor.relation === "touching") {
      issues.push(`Existe contato não autorizado com “${neighbor.name}”.`);
    }
    // A gap the designer fixed explicitly is theirs; only the plan range applies.
    if (decision.source === "user" || decision.source === "reference") continue;
    const assessment = assessVerticalRelation({
      rule,
      gapMm: neighbor.verticalGapMm,
      touching: neighbor.relation === "touching",
      upperLabel,
      lowerLabel,
    });
    if (assessment.message) issues.push(assessment.message);
  }

  if (suspendedSupport) {
    for (const neighbor of installation.nearestElements) {
      if (neighbor.position !== "below" || neighbor.planOverlapMm <= 1) continue;
      const neighborKind = classifyInstallationTypology(neighborDescriptor(neighbor.name)).kind;
      const pairRule = verticalClearanceRule({ upper: elementTypology.kind, lower: neighborKind, relation: "above", support: "wall" });
      // Contact is legitimate when the typology pair allows it (a panel on a
      // rack, a countertop on a base) or when the plan declares a stack
      // resting on the element. A suspended box resting on furniture because
      // the plan merely said allowContact is not.
      const allowedContact = Boolean(pairRule?.contactAllowed)
        || verticalDecisions.some((decision) => decision.allowContact
          && decision.support === "element"
          && textRefersTo(decision.reference, neighbor.name));
      if (neighbor.relation === "touching" && !allowedContact) {
        issues.push(`Elemento suspenso encostado em “${neighbor.name}” sem apoio ou contato autorizado no plano.`);
      }
      if (assessedNeighbors.has(neighbor.name) || descriptors.enforceTypology === false) continue;
      // The plan may be silent about this neighbour (or have no plan at all,
      // as in a direct request). The typology rule still applies to any
      // suspended element: resting on, or hovering a hand's width above, the
      // unit below is a defect a professional never ships.
      const rule = verticalClearanceRule({
        upper: elementTypology.kind,
        lower: classifyInstallationTypology(neighborDescriptor(neighbor.name)).kind,
        relation: "above",
        support: installation.support.expected === "ceiling" ? "ceiling" : "wall",
      });
      const assessment = assessVerticalRelation({
        rule,
        gapMm: neighbor.verticalGapMm,
        touching: neighbor.relation === "touching",
        upperLabel: `“${elementName}”`,
        lowerLabel: `“${neighbor.name}”`,
      });
      if (assessment.message && !allowedContact) issues.push(assessment.message);
    }
  }
  return [...new Set(issues)];
}

export function issuesRequirePlanAmendment(issues: ReadonlyArray<string>): boolean {
  return issues.some((issue) => issue.includes(PLAN_AMENDMENT_REQUIRED_MARKER));
}

/**
 * Decides whether a panoramic observation may send the turn back to correction.
 * Aesthetic judgment is deliberately weaker than an explicit briefing.
 */
export function environmentIssueDisposition(
  plan: TaskPlan | undefined,
  issue: EnvironmentReviewIssue,
  elementName: string,
  changedIds: ReadonlySet<string>,
  boundAuthority: "explicit" | "agent" | null = null,
): EnvironmentIssueDisposition {
  if (!changedIds.has(issue.elementId)) return { actionable: false, reason: "outside-turn" };
  if (issue.kind === "technical") return { actionable: true, reason: "changed-technical" };
  if (issue.kind === "brief_mismatch") return { actionable: true, reason: "changed-brief-mismatch" };
  if (!plan || plan.designFreedom === "strict") return { actionable: false, reason: "authority-unproven" };
  if (boundAuthority === "explicit") return { actionable: false, reason: "explicitly-protected" };
  if (boundAuthority === "agent") return { actionable: true, reason: "agent-owned" };

  const matching = plan.decisionAuthorities.filter((decision) => (
    decision.attribute === issue.attribute && textRefersTo(decision.subject, elementName)
  ));
  if (matching.some((decision) => decision.authority === "explicit")) {
    return { actionable: false, reason: "explicitly-protected" };
  }
  if (matching.some((decision) => decision.authority === "agent")) {
    return { actionable: true, reason: "agent-owned" };
  }
  // A visual critic may describe a cross-cutting composition defect as
  // appearance/coherence even when the plan stored the underlying authorship
  // under product, dimensions, position or relation. If this element contains
  // agent-owned visual decisions and the reported attribute is not explicitly
  // protected, the review must be able to return that choice for correction.
  if (elementRequiresAgentDesignReview(plan, elementName)) {
    return { actionable: true, reason: "agent-owned" };
  }
  return { actionable: false, reason: "authority-unproven" };
}

export function elementRequiresAgentDesignReview(plan: TaskPlan | undefined, elementName: string): boolean {
  if (!plan || plan.designFreedom === "strict") return false;
  const visualDesignAttributes = new Set(["position", "dimensions", "orientation", "material", "product", "quantity", "relation", "appearance"]);
  const matching = plan.decisionAuthorities.filter((decision) => (
    visualDesignAttributes.has(decision.attribute) && textRefersTo(decision.subject, elementName)
  ));
  return matching.some((decision) => decision.authority === "agent"
    && !matching.some((candidate) => candidate.attribute === decision.attribute && candidate.authority === "explicit"));
}

export function requiredAgentDesignReviewAspects(
  plan: TaskPlan | undefined,
  elementName: string,
): ProfessionalDesignAspect[] {
  if (!plan || plan.designFreedom === "strict") return [];
  const explicitAttributes = new Set(plan.decisionAuthorities
    .filter((decision) => decision.authority === "explicit" && textRefersTo(decision.subject, elementName))
    .map((decision) => decision.attribute));
  const aspects = plan.designResolutions
    .filter((resolution) => (
      resolution.authority === "agent"
      && textRefersTo(resolution.subject, elementName)
      && !explicitAttributes.has(resolution.attribute)
    ))
    .flatMap((resolution) => resolution.reviewCriteria.map((criterion) => criterion.aspect));
  return [...new Set(aspects)];
}

export function professionalEnvironmentJudgmentIssues(input: {
  plan: TaskPlan | undefined;
  changedIds: string[];
  comparedChangedIds: string[];
  checks: Record<string, unknown>;
  wouldReviseBeforePresentation: boolean;
  issueElementIds: string[];
  /** Texto do julgamento (rationale, summary, strongestConcern) para conferir se a ergonomia foi avaliada com cotas. */
  judgmentText?: string;
  /** Há elemento suspenso ou de uso alterado nesta rodada cuja ergonomia depende de cota. */
  ergonomicsRelevant?: boolean;
}): string[] {
  const issues: string[] = [];
  const compared = new Set(input.comparedChangedIds);
  const missing = input.changedIds.filter((id) => !compared.has(id));
  if (missing.length) {
    issues.push(`A crítica profissional não comparou todos os alvos alterados com o conjunto: ${missing.join(", ")}.`);
  }
  // "Ergonomia: pass" sem um número não é avaliação, é carimbo. Quando há um
  // alvo cuja qualidade depende de cota (suspenso, superfície de uso), o
  // julgamento precisa citar ao menos uma cota em milímetros.
  if (input.ergonomicsRelevant && input.checks.ergonomics === "pass") {
    const citesCota = /\d[\d.\s]*\s?mm/i.test(input.judgmentText ?? "");
    if (!citesCota) {
      issues.push("A avaliação de ergonomia (ergonomics=pass) não cita nenhuma cota: registre a altura de uso, o alcance ou o vão em milímetros que sustentam o julgamento, ou marque not_applicable.");
    }
  }
  const hasAgentVisualAuthorship = Boolean(input.plan && input.plan.designFreedom !== "strict"
    && input.plan.designResolutions.some((resolution) => (
      resolution.authority === "agent"
      && ["position", "dimensions", "orientation", "material", "product", "quantity", "relation", "appearance"].includes(resolution.attribute)
    )));
  if (hasAgentVisualAuthorship) {
    for (const check of ["composition", "scale", "proportion", "alignment", "coherence"]) {
      if (input.checks[check] === "not_applicable" || input.checks[check] === undefined) {
        issues.push(`A revisão de autoria precisa avaliar explicitamente ${check}; “${String(input.checks[check] ?? "ausente")}" não encerra a qualidade profissional.`);
      }
    }
  }
  if (input.wouldReviseBeforePresentation && input.issueElementIds.length === 0) {
    issues.push("O revisor declarou que ainda faria ajustes antes da apresentação, mas não atribuiu a correção a nenhum alvo alterado.");
  }
  if (!input.wouldReviseBeforePresentation && input.issueElementIds.length > 0) {
    issues.push("O laudo contém correções acionáveis, mas declarou que não revisaria o resultado antes da apresentação.");
  }
  return issues;
}

export interface StructuredFunctionReview {
  expected: "closed" | "open";
  satisfied: boolean;
  evidence: string;
}

/**
 * Reconciles a visual function verdict with the component's structured state.
 * Images remain authoritative for appearance; dynamic attributes are
 * authoritative for whether an interaction such as a door is actually open.
 */
export function structuredFunctionReview(
  plan: TaskPlan | undefined,
  elementName: string,
  interactions: ReadonlyArray<{ state: "open" | "closed" | "unknown" }>,
): StructuredFunctionReview | null {
  if (!plan || interactions.length === 0) return null;
  const functionTexts = [
    ...plan.designResolutions
      .filter((resolution) => resolution.attribute === "function" && textRefersTo(resolution.subject, elementName))
      .map((resolution) => resolution.decision),
    ...plan.decisionAuthorities
      .filter((decision) => decision.attribute === "function" && textRefersTo(decision.subject, elementName))
      .map((decision) => decision.requirement),
  ];
  const normalized = functionTexts.join(" ").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const expected = /\b(fechar|fechad[ao]s?|closed?)\b/.test(normalized)
    ? "closed"
    : /\b(abrir|abert[ao]s?|open)\b/.test(normalized)
      ? "open"
      : null;
  if (!expected) return null;
  const satisfied = expected === "closed"
    ? interactions.every((interaction) => interaction.state === "closed")
    : interactions.some((interaction) => interaction.state === "open");
  const states = interactions.map((interaction) => interaction.state);
  return {
    expected,
    satisfied,
    evidence: `${interactions.length} interação(ões) dinâmica(s): ${states.join(", ")}.`,
  };
}

const GENERIC_SUBJECT_TERMS = new Set(["novo", "nova", "novos", "novas", "existente", "existentes", "movido", "movida", "modulo", "modulos", "movel", "moveis", "elemento", "elementos", "conjunto", "conjuntos"]);

/**
 * Cor, tom e lado distinguem variantes de um mesmo tipo, nunca identificam o
 * tipo: "claro" compartilhado não faz de um aéreo e um móvel baixo o mesmo
 * sujeito (20/08 02:34 — a cota do aéreo foi cobrada do móvel de piso por
 * causa do adjetivo, quatro recusas de etapa e parada técnica), e "clara" ×
 * "escura" sobre o mesmo substantivo são elementos diferentes.
 */
const SUBJECT_QUALIFIER_TERMS = new Set([
  "claro", "clara", "claros", "claras", "escuro", "escura", "escuros", "escuras",
  "branco", "branca", "brancos", "brancas", "preto", "preta", "pretos", "pretas",
  "marrom", "marrons", "cinza", "cinzas", "bege", "beges", "amadeirado", "amadeirada",
  "dourado", "dourada", "prateado", "prateada", "fosco", "fosca", "brilhante", "brilhantes",
  "esquerdo", "esquerda", "esquerdos", "esquerdas", "direito", "direita", "direitos", "direitas",
  "central", "centrais", "frontal", "frontais", "traseiro", "traseira", "lateral", "laterais",
]);

function subjectTermSets(value: string): { content: string[]; qualifiers: string[] } {
  const terms = value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("pt-BR")
    .split(/[^a-z0-9]+/)
    .filter((term) => term.length >= 3 && !GENERIC_SUBJECT_TERMS.has(term));
  return {
    content: terms.filter((term) => !SUBJECT_QUALIFIER_TERMS.has(term)),
    qualifiers: terms.filter((term) => SUBJECT_QUALIFIER_TERMS.has(term)),
  };
}

/**
 * Dois nomes de sujeito referem-se ao mesmo elemento quando os termos de
 * conteúdo (tipologia, produto) coincidem em pelo menos um — e qualificadores
 * declarados dos dois lados sem interseção vetam o casamento mesmo assim.
 */
export function subjectNamesAgree(left: string, right: string): boolean {
  const normalize = (value: string) => value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("pt-BR")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
  const normalizedLeft = normalize(left);
  const normalizedRight = normalize(right);
  if (!normalizedLeft || !normalizedRight) return false;
  if (normalizedLeft === normalizedRight) return true;
  if (normalizedLeft.length >= 6 && normalizedRight.length >= 6
    && (normalizedLeft.includes(normalizedRight) || normalizedRight.includes(normalizedLeft))) return true;
  const a = subjectTermSets(left);
  const b = subjectTermSets(right);
  const sharedQualifiers = a.qualifiers.filter((term) => b.qualifiers.includes(term));
  if (a.content.length && b.content.length) {
    if (!a.content.some((term) => b.content.includes(term))) return false;
    if (a.qualifiers.length && b.qualifiers.length && !sharedQualifiers.length) return false;
    return true;
  }
  // Só quando NENHUM dos lados tem termo de conteúdo ("módulo claro" ×
  // "módulo escuro") o qualificador compartilhado decide; um nome só de
  // qualificador nunca reivindica um nome tipado — a ambiguidade não casa.
  if (!a.content.length && !b.content.length) return sharedQualifiers.length > 0;
  return false;
}

function textRefersTo(reference: string, actualName: string): boolean {
  const { content, qualifiers } = subjectTermSets(reference);
  if (content.length === 0 && qualifiers.length === 0) return true;
  return subjectNamesAgree(reference, actualName);
}

function roundMm(value: number): number {
  return Math.round(value * 10) / 10;
}
